import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import QMSLayout from "../components/QMSLayout";
import OrgSelector from "../components/OrgSelector";
import { API, useAuth } from "../App";
import {
  FileCheck, Loader2, Plus, ChevronRight, ChevronDown,
  Download, CheckCircle, Clock, AlertCircle, Sparkles, ArrowRight, Search
} from "lucide-react";

const QualityAgreementPage = () => {
  const { t } = useTranslation('operations');
  const { user } = useAuth();
  const navigate = useNavigate();
  const [agreements, setAgreements] = useState([]);
  const [standards, setStandards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [selected, setSelected] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    client_name: "", org_id: "", org_name: "", vendor_id: "", standards: [], scope: "",
  });

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    try {
      const [agRes, stRes] = await Promise.all([
        axios.get(`${API}/quality-agreements`, { withCredentials: true }),
        axios.get(`${API}/standards`, { withCredentials: true }),
      ]);
      setAgreements(agRes.data);
      setStandards(stRes.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  useEffect(() => {
    if (!selected || selected.status !== "generating") return;
    const interval = setInterval(async () => {
      try {
        const res = await axios.get(`${API}/quality-agreements/${selected.agreement_id}`, { withCredentials: true });
        if (res.data.status !== "generating") {
          setSelected(res.data);
          fetchAll();
          clearInterval(interval);
        }
      } catch (e) { clearInterval(interval); }
    }, 5000);
    return () => clearInterval(interval);
  }, [selected]);

  const toggleStandard = (stdName) => {
    setForm(p => ({
      ...p,
      standards: p.standards.includes(stdName) ? p.standards.filter(s => s !== stdName) : [...p.standards, stdName]
    }));
  };

  const handleGenerate = async () => {
    if (!form.client_name || !form.standards.length || !form.scope) return;
    setGenerating(true);
    try {
      const res = await axios.post(`${API}/quality-agreement/generate`, form, { withCredentials: true });
      setSelected(res.data);
      setShowForm(false);
      setForm({ client_name: "", org_id: "", org_name: "", vendor_id: "", standards: [], scope: "" });
      fetchAll();
    } catch (e) { console.error(e); }
    setGenerating(false);
  };

  const StatusBadge = ({ status }) => {
    const config = {
      completed: { icon: CheckCircle, color: "#4ADE80", label: "Completed" },
      generating: { icon: Clock, color: "#FBBF24", label: t('common:status.generating') },
      failed: { icon: AlertCircle, color: "#F87171", label: "Failed" },
    };
    const c = config[status] || config.failed;
    return (
      <span className="inline-flex items-center gap-1.5 text-xs font-medium" style={{ color: c.color }}>
        <c.icon size={14} /> {c.label}
      </span>
    );
  };

  return (
    <QMSLayout title={t('quality.title')}>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="font-['Syne'] text-2xl font-bold text-white" data-testid="qa-title">{t('quality.title')}</h2>
          <p className="text-[#8B95A5] text-sm mt-1">{t('quality.subtitle')}</p>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={() => navigate("/gap-assessment")}
            className="flex items-center gap-2 px-3 py-2 bg-[rgba(54,169,225,0.1)] text-[#38BDF8] text-sm rounded-[3px] hover:bg-[rgba(54,169,225,0.15)] transition-colors"
            data-testid="qa-goto-gap-btn">
            <Search size={14} /> Gap Assessment
            <ArrowRight size={14} />
          </button>
          <button onClick={() => setShowForm(true)} className="btn-primary flex items-center gap-2" data-testid="qa-new-btn">
            <Plus size={16} /> {t('quality.newAgreement')}
          </button>
        </div>
      </div>

      {showForm && (
        <div className="qms-card mb-6" data-testid="qa-form">
          <div className="qms-card-body space-y-4">
            <h3 className="font-['Syne'] font-semibold text-white flex items-center gap-2">
              <Sparkles size={18} className="text-[#E8A838]" /> {t('quality.title')}
            </h3>
            <OrgSelector
              value={form.org_id}
              onChange={(orgId, orgName) => setForm(p => ({...p, org_id: orgId, org_name: orgName, client_name: orgName || p.client_name}))}
              showVendorId={true}
              vendorId={form.vendor_id}
              onVendorIdChange={v => setForm(p => ({...p, vendor_id: v}))}
            />
            {!form.org_id && (
              <input
                type="text" placeholder={t('vendor.companyName', { ns: 'operations' })}
                value={form.client_name} onChange={e => setForm(p => ({ ...p, client_name: e.target.value }))}
                className="qms-input w-full" data-testid="qa-client-input"
              />
            )}
            <div>
              <label className="text-[#8B95A5] text-xs uppercase tracking-wider mb-2 block">Select Standards (multi-select)</label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2 max-h-48 overflow-y-auto bg-[#111318] border border-[rgba(255,255,255,0.1)] rounded-[3px] p-3" data-testid="qa-standards-select">
                {standards.map(std => (
                  <label
                    key={std.id}
                    className={`flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer text-xs transition-colors ${
                      form.standards.includes(std.name) ? "bg-[rgba(54,169,225,0.15)] text-[#38BDF8]" : "text-[#CBD5E1] hover:bg-[rgba(255,255,255,0.03)]"
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={form.standards.includes(std.name)}
                      onChange={() => toggleStandard(std.name)}
                      className="rounded border-[rgba(255,255,255,0.2)] bg-transparent text-[#36A9E1]"
                    />
                    <span className="truncate">{std.name}</span>
                  </label>
                ))}
              </div>
              {form.standards.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-2">
                  {form.standards.map(name => (
                    <span key={name} className="text-xs bg-[rgba(54,169,225,0.15)] text-[#38BDF8] px-2 py-0.5 rounded-full flex items-center gap-1">
                      {name}
                      <button onClick={() => toggleStandard(name)} className="hover:text-white">&times;</button>
                    </span>
                  ))}
                </div>
              )}
            </div>
            <textarea
              placeholder="Agreement scope and context..."
              value={form.scope} onChange={e => setForm(p => ({ ...p, scope: e.target.value }))}
              className="qms-input w-full h-24" data-testid="qa-scope-input"
            />
            <div className="flex gap-3">
              <button onClick={handleGenerate} disabled={generating || !form.client_name || !form.standards.length}
                className="btn-primary flex items-center gap-2" data-testid="qa-generate-btn">
                {generating ? <><Loader2 size={16} className="animate-spin" /> Generating...</> : <><Sparkles size={16} /> Generate</>}
              </button>
              <button onClick={() => setShowForm(false)} className="btn-ghost">{t('common:actions.cancel')}</button>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Agreement List */}
        <div className="lg:col-span-1 space-y-3">
          {loading ? (
            <div className="flex justify-center py-12"><Loader2 className="animate-spin text-[#36A9E1]" size={24} /></div>
          ) : agreements.length === 0 ? (
            <div className="qms-card"><div className="qms-card-body text-center text-[#8B95A5] py-8">
              <FileCheck size={32} className="mx-auto mb-2 opacity-50" />
              <p className="text-sm">No quality agreements yet. Click "New Agreement" to get started.</p>
            </div></div>
          ) : agreements.map(a => (
            <div key={a.agreement_id}
              onClick={() => setSelected(a)}
              className={`qms-card cursor-pointer transition-all hover:border-[#36A9E1]/30 ${selected?.agreement_id === a.agreement_id ? "border-[#36A9E1]/50" : ""}`}
              data-testid={`qa-item-${a.agreement_id}`}>
              <div className="qms-card-body py-3">
                <div className="flex items-center justify-between">
                  <h4 className="text-white text-sm font-medium truncate">{a.client_name}</h4>
                  <StatusBadge status={a.status} />
                </div>
                <p className="text-[#8B95A5] text-xs mt-1 truncate">{(a.standards || []).join(", ")}</p>
                {a.source_assessment_id && (
                  <span className="text-[10px] text-[#A78BFA] mt-1 block">Linked to assessment</span>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Agreement Detail */}
        <div className="lg:col-span-2">
          {selected ? (
            <div className="qms-card" data-testid="qa-detail">
              <div className="qms-card-body">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-['Syne'] text-lg font-semibold text-white">{selected.client_name}</h3>
                  <StatusBadge status={selected.status} />
                </div>
                {selected.source_assessment_id && (
                  <div className="mb-4 p-3 bg-[rgba(167,139,250,0.05)] border border-[rgba(167,139,250,0.15)] rounded-[3px]">
                    <p className="text-[#A78BFA] text-xs flex items-center gap-1">
                      <Search size={12} /> Generated from Gap Assessment
                    </p>
                  </div>
                )}
                {selected.status === "generating" && (
                  <div className="flex items-center gap-3 text-[#FBBF24] py-8 justify-center">
                    <Loader2 size={20} className="animate-spin" />
                    <span className="text-sm">AI is drafting your quality agreement...</span>
                  </div>
                )}
                {selected.status === "completed" && selected.content && (
                  <div className="space-y-4">
                    {selected.content.title && (
                      <h4 className="text-[#36A9E1] font-semibold">{selected.content.title}</h4>
                    )}
                    {(selected.content.sections || []).map((sec, i) => (
                      <div key={i} className="border-l-2 border-[#36A9E1]/30 pl-4">
                        <h5 className="text-white font-medium text-sm mb-1">{sec.heading}</h5>
                        <p className="text-[#8B95A5] text-xs whitespace-pre-line">{sec.content}</p>
                      </div>
                    ))}
                  </div>
                )}
                {selected.status === "failed" && (
                  <div className="text-center py-8 text-[#F87171]">
                    <AlertCircle size={32} className="mx-auto mb-2" />
                    <p className="text-sm">Generation failed. Please try again.</p>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="qms-card"><div className="qms-card-body text-center text-[#8B95A5] py-16">
              Select an agreement to view details
            </div></div>
          )}
        </div>
      </div>
    </QMSLayout>
  );
};

export default QualityAgreementPage;
